import { MarkerType, type Edge, type Node } from 'reactflow'
import { branchPosition, SNAKE_EDGE_STYLE, snakeEdgeHandles, snakePosition } from '../workflowGraph'
import type { WorkflowGraphNodeData } from '../workflowTypes'
import type { Plan, RunState } from '../v4/contracts'
import { stepAction } from '../v4/step-label'
import { runHistory } from './runHistory'
import { LIVE_CARD_HEIGHT, LIVE_CARD_WIDTH, type LiveCardNodeData } from '../builder/LiveCardNode'
import type { LiveCardSpec } from './liveCards'

/**
 * The old builder's canvas over a v4 run: one card per step of the run's latest plan, in plan
 * order on the snake layout, with the status the run's trace and events give the step. The plan
 * is the engine's (`RunState.trace.planVersions`, the last version); nothing here is authored.
 */

type PlanStep = Plan['steps'][number]

/** Node ids carry the step id, so a click can find the step in the run again. */
export const RUN_NODE_PREFIX = 'run:'
export const runNodeId = (stepId: string): string => `${RUN_NODE_PREFIX}${stepId}`
export const stepIdOfNode = (nodeId: string): string | null =>
  nodeId.startsWith(RUN_NODE_PREFIX) ? nodeId.slice(RUN_NODE_PREFIX.length) : null

/** The plan the canvas draws: the latest version, or null before the engine has proposed one. */
export function latestPlan(run: RunState | null): Plan | null {
  return run?.trace.planVersions.at(-1) ?? null
}

/** Whether the drawn plan version has been approved (a later version needs its own approval). */
export function planApproved(run: RunState, plan: Plan): boolean {
  return run.events.some((e) => e.type === 'plan_approved' && e.planVersion === plan.version)
}

/** The model does the work of these steps; an ask waits on a person, a deliver writes the answer. */
const AGENT_KINDS = new Set<PlanStep['kind']>(['triage', 'gather', 'calc', 'reason', 'decide'])

/**
 * A finished step's trace outcome that is not a failure. A read records its tool's outcome (`ok`,
 * `empty`); a calc, reason, decide or person step that ran records `done`; a decide step that
 * sends the plan to a new version records `new version`; and a step the run stopped before records
 * `stopped` (engine run/executor.ts).
 */
const TRACED_STATUS = new Map<string, string>([
  ['ok', 'completed'],
  ['done', 'completed'],
  ['new version', 'completed'],
  ['empty', 'completed_with_unknowns'],
  ['stopped', 'skipped'],
])

/**
 * A step's status in the words the old step node renders (WorkflowStepNode's live statuses). What
 * the run did with the step comes first, in whichever plan version it ran: its trace outcome once
 * it has one (see TRACED_STATUS; anything else, such as `error`, `unavailable` or `failed`, →
 * failed), else the status a later version carried it with (done, skipped, failed). A new version
 * keeps the steps that ran, and their trace stays on the version they ran in, so they keep their
 * status while the new version awaits approval. Otherwise `draft` until the plan is approved; then
 * the run's step events (parked → waiting for the user, started → running), else pending; and
 * cancelled when the run was cancelled before the step ran.
 */
export function runStepStatus(run: RunState, plan: Plan, step: PlanStep): string {
  const traced = run.trace.steps.find((s) => s.stepId === step.id)
  if (traced?.endedAt || (traced && traced.outcome !== 'running' && traced.outcome !== 'pending')) {
    return TRACED_STATUS.get(traced.outcome) ?? 'failed'
  }
  if (step.status === 'done') return 'completed'
  if (step.status === 'skipped') return 'skipped'
  if (step.status === 'failed') return 'failed'
  if (!planApproved(run, plan)) return 'draft'
  let last: 'started' | 'parked' | 'done' | null = null
  for (const e of run.events) {
    if (e.stepId !== step.id) continue
    if (e.type === 'step_started' || e.type === 'resumed') last = 'started'
    else if (e.type === 'step_parked') last = 'parked'
    else if (e.type === 'step_done') last = 'done'
  }
  if (last === 'parked') return 'waiting_for_user'
  if (last === 'started') return 'running'
  if (last === 'done') return 'completed'
  if (run.status === 'finished' && run.outcome === 'cancelled') return 'cancelled'
  return 'pending'
}

/** Node ids of a later version's marker card. */
export const versionNodeId = (version: number): string => `version:${version}`

type Slot = { x: number; y: number; column: number; row: number }

function edge(from: string, to: string, slots: Map<string, Slot>, handles = snakeEdgeHandles(slots.get(from)!, slots.get(to)!)): Edge {
  return {
    id: `${from}->${to}`,
    source: from,
    target: to,
    ...handles,
    type: 'smoothstep',
    style: SNAKE_EDGE_STYLE,
    markerEnd: { type: MarkerType.ArrowClosed, color: SNAKE_EDGE_STYLE.stroke },
  }
}

/**
 * The handles for the line from the step that made a version down to its marker: straight down
 * when nothing sits between them in that column, else out of the right side and down beside the
 * column, so the line does not run through the cards below the step.
 */
function besideColumn(from: string, to: string, slots: Map<string, Slot>): { sourceHandle: string; targetHandle: string } {
  const a = slots.get(from)!
  const b = slots.get(to)!
  const between = [...slots.values()].some((s) => s.column === a.column && s.row > a.row && s.row < b.row)
  return between ? { sourceHandle: 's-right', targetHandle: 't-right' } : snakeEdgeHandles(a, b)
}

/**
 * The run as the old canvas's nodes and edges; empty before a plan exists. The first drawn plan
 * version runs on the snake layout. Each later version (runHistory) hangs below it as a branch from
 * the step whose finding made it: a marker card saying what was found, then the steps the version
 * added. Steps a version left out before they ran stay where they were, marked replaced; steps
 * that ran keep their place and status whichever version kept them.
 */
export function buildRunGraph(run: RunState | null): {
  nodes: Array<Node<WorkflowGraphNodeData>>
  edges: Edge[]
} {
  const flow = runHistory(run)
  if (!run || flow.steps.length === 0) return { nodes: [], edges: [] }

  const first = flow.steps[0].introducedIn
  const slots = new Map<string, Slot>()
  flow.steps.filter((f) => f.introducedIn === first).forEach((f, i) => slots.set(runNodeId(f.step.id), snakePosition(i)))
  const lowest = () => Math.max(...[...slots.values()].map((s) => s.row))
  for (const mark of flow.marks) {
    const trigger = mark.cause.kind === 'step' ? slots.get(runNodeId(mark.cause.stepId)) : undefined
    const band = lowest() + 1
    ;[versionNodeId(mark.version), ...mark.added.map(runNodeId)].forEach((id, i) => slots.set(id, branchPosition(trigger?.column ?? 0, band, i)))
  }

  const titleOf = new Map(flow.steps.map((f) => [f.step.id, f.step.title]))
  const nodes: Array<Node<WorkflowGraphNodeData>> = flow.steps.map((f) => {
    const step = f.step
    const status = f.replacedIn ? 'replaced' : runStepStatus(run, f.plan, step)
    const slot = slots.get(runNodeId(step.id))!
    const fresh = f.introducedIn !== first && (status === 'draft' || status === 'pending')
    return {
      id: runNodeId(step.id),
      type: 'workflowStep',
      position: { x: slot.x, y: slot.y },
      data: {
        kind: 'step',
        stepId: step.id,
        label: `${step.title}\n${status}`,
        title: step.title,
        subtitle: stepAction(step),
        status,
        isAgent: AGENT_KINDS.has(step.kind),
        // An ask step is the engine's own "human in the loop"; nothing on it is configurable.
        hitl: step.kind === 'ask',
        hitlReadonly: true,
        clickable: true,
        ...(f.replacedIn ? { replacedIn: f.replacedIn } : {}),
        ...(fresh ? { newIn: f.introducedIn } : {}),
      },
    }
  })
  for (const mark of flow.marks) {
    const slot = slots.get(versionNodeId(mark.version))!
    const cause = mark.cause.kind === 'step' ? { kind: 'step' as const, stepTitle: titleOf.get(mark.cause.stepId) ?? mark.cause.stepId, ...(mark.cause.file ? { file: true as const } : {}) } : mark.cause
    nodes.push({
      id: versionNodeId(mark.version),
      type: 'workflowStep',
      position: { x: slot.x, y: slot.y },
      data: { kind: 'version', label: `v${mark.version}`, title: `v${mark.version}`, status: 'version', versionMark: { version: mark.version, cause, finding: mark.finding, reason: mark.reason } },
    })
  }

  // Each version's own dependencies, not a chain. A version's added steps hang from its marker,
  // which hangs from the step that made it; their inputs on earlier steps are not drawn across.
  const edges: Edge[] = []
  const introduced = new Map(flow.steps.map((f) => [f.step.id, f.introducedIn]))
  for (const f of flow.steps) {
    for (const dep of f.step.dependsOn) {
      if (introduced.get(dep) === f.introducedIn && slots.has(runNodeId(dep))) edges.push(edge(runNodeId(dep), runNodeId(f.step.id), slots))
    }
  }
  for (const mark of flow.marks) {
    const marker = versionNodeId(mark.version)
    const trigger = mark.cause.kind === 'step' ? runNodeId(mark.cause.stepId) : null
    if (trigger && slots.has(trigger)) edges.push(edge(trigger, marker, slots, besideColumn(trigger, marker, slots)))
    const added = new Set(mark.added)
    for (const id of mark.added) {
      const step = flow.steps.find((f) => f.step.id === id)!.step
      if (!step.dependsOn.some((d) => added.has(d))) edges.push(edge(marker, runNodeId(id), slots))
    }
  }
  return { nodes, edges }
}

/** Node id of a live card. */
export const liveCardNodeId = (cardId: string): string => `livecard:${cardId}`

const LIVE_CARD_GAP = 40

/**
 * The run's live cards as canvas nodes, to the left of the whole plan (clear of every step) at the
 * height of the step each came from, found by its id or else its title. A card whose step is
 * unknown goes to the top. Cards that would overlap are stacked downwards in step order, so several
 * on one step form a column. Each hangs from its step by a dashed edge.
 */
export function withLiveCards(
  graph: { nodes: Node[]; edges: Edge[] },
  cards: LiveCardSpec[],
  onRemove: (id: string) => void
): { nodes: Node[]; edges: Edge[] } {
  const steps = graph.nodes.filter((n) => stepIdOfNode(n.id) !== null)
  if (cards.length === 0 || steps.length === 0) return graph
  const left = Math.min(...steps.map((n) => n.position.x)) - LIVE_CARD_WIDTH - LIVE_CARD_GAP * 2
  const stepOf = (c: LiveCardSpec): Node =>
    steps.find((n) => stepIdOfNode(n.id) === c.stepId) ?? steps.find((n) => (n.data as WorkflowGraphNodeData).title === c.title) ?? steps[0]
  const placed = cards
    .map((card, order) => ({ card, order, step: stepOf(card) }))
    .sort((a, b) => a.step.position.y - b.step.position.y || a.order - b.order)
  let floor = -Infinity
  const nodes: Node[] = []
  const edges: Edge[] = []
  for (const { card, step } of placed) {
    const y = Math.max(step.position.y, floor)
    floor = y + LIVE_CARD_HEIGHT + LIVE_CARD_GAP
    const id = liveCardNodeId(card.id)
    const data: LiveCardNodeData = { documentId: card.documentId, tags: card.tags, title: card.title, onRemove: () => onRemove(card.id) }
    nodes.push({ id, type: 'liveCard', position: { x: left, y }, data, draggable: true })
    edges.push({
      id: `${id}->${step.id}`,
      source: id,
      target: step.id,
      sourceHandle: 's-right',
      targetHandle: 't-left',
      type: 'smoothstep',
      style: { ...SNAKE_EDGE_STYLE, strokeDasharray: '5 4' },
    })
  }
  return { nodes: [...graph.nodes, ...nodes], edges: [...graph.edges, ...edges] }
}
